import { supabase } from "../../lib/supabaseClient";
import { isWebView } from "../../lib/isWebView";

/**
 * Detect if we're inside the Android WebView with custom UA
 */
function isAppWebView(): boolean {
  const ua = navigator.userAgent || '';
  return ua.includes('MyAppWebView') ||          // Custom UA suffix from Android
         (ua.includes('Android') && ua.includes('wv')); // Generic WebView hint
}

/**
 * Get the current domain's auth finish URL
 */
function getAuthFinishUrl(): string {
  // Use current domain for OAuth callbacks to maintain session consistency
  return `${window.location.origin}/auth/finish`;
}

/**
 * Resolve the Edge Function redirect URL robustly.
 * Prefer VITE_EDGE_REDIRECT_URL. If missing, derive from VITE_SUPABASE_URL.
 */
function resolveEdgeRedirect(): string {
  const explicit = import.meta.env.VITE_EDGE_REDIRECT_URL?.toString().trim();
  if (explicit) return explicit.replace(/\/+$/, ""); // strip trailing slash

  const supaUrl = import.meta.env.VITE_SUPABASE_URL?.toString().trim();
  if (!supaUrl) {
    throw new Error(
      "Missing VITE_EDGE_REDIRECT_URL and VITE_SUPABASE_URL; cannot resolve Edge Function URL."
    );
  }

  // Convert https://<ref>.supabase.co → https://<ref>.functions.supabase.co
  const m = supaUrl.match(/^https:\/\/([a-z0-9-]+)\.supabase\.co\/?$/i);
  if (!m) {
    throw new Error(
      `VITE_SUPABASE_URL doesn't look like a hosted Supabase URL: ${supaUrl}`
    );
  }
  const ref = m[1];
  return `https://${ref}.functions.supabase.co/mobile-auth-complete`;
}

const EDGE_REDIRECT = resolveEdgeRedirect();

/**
 * Build the Supabase Google authorize URL manually.
 * Useful in WebViews so we can force opening in an external browser via _blank.
 */
function buildAuthorizeUrl(params?: { redirect?: string }) {
  const supaUrl = import.meta.env.VITE_SUPABASE_URL as string;
  if (!supaUrl) throw new Error("VITE_SUPABASE_URL is not set.");

  // Optional: carry a redirect path in 'state' so your /auth/webview-complete can honor it
  const state =
    params?.redirect && params.redirect.startsWith("/")
      ? `redirect=${encodeURIComponent(params.redirect)}`
      : "";

  const q: Record<string, string> = {
    provider: "google",
    redirect_to: getAuthFinishUrl(),
    flow_type: "pkce",
    access_type: "offline",
    prompt: "consent",
  };
  if (state) q.state = state;

  const usp = new URLSearchParams(q);
  return `${supaUrl.replace(/\/+$/, "")}/auth/v1/authorize?${usp.toString()}`;
}

/**
 * Starts Google OAuth login with WebView compatibility (Variant A).
 * - In WebView: open system browser via window.open('_blank') to the authorize URL targeting the Edge Function.
 * - On normal web: use supabase-js helper as usual.
 */
export async function startGoogleLogin(options?: { redirect?: string }) {
  try {
    const inAppWebView = isAppWebView();
    const inGenericWebView = isWebView();
    console.log("[auth] startGoogleLogin; appWebView:", inAppWebView, "genericWebView:", inGenericWebView);

    // Pick the right redirect target per environment
    const redirectTo = inAppWebView
      ? 'amalieai://auth-callback'                    // App deep link
      : getAuthFinishUrl();                           // Current domain's auth finish URL

    console.log("[auth] Using redirect URL:", redirectTo);

    if (inGenericWebView && !inAppWebView) {
      // Generic WebView (not our app) - use external browser approach
      const authorizeUrl = buildAuthorizeUrl({ redirect: options?.redirect });
      console.log("[auth] Generic WebView → opening external browser:", authorizeUrl);

      const opened = window.open(authorizeUrl, "_blank", "noopener,noreferrer");
      if (!opened) {
        window.location.href = authorizeUrl;
      }
      return;
    }

    // App WebView or normal web flow - use PKCE
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        flowType: 'pkce',
        queryParams: {
          access_type: "offline",
          prompt: "consent",
        },
      },
    });

    if (error) {
      console.error("[auth] OAuth error:", error);
      throw error;
    }
    return data;
  } catch (err: any) {
    console.error("[auth] Error starting Google login:", err);
    throw new Error(err?.message || "Failed to start Google login");
  }
}

/**
 * Returns the redirect URL the flow will use (useful for debugging/UI).
 */
export function getRedirectUrl(): string {
  return getAuthFinishUrl();
}

/**
 * Checks if OAuth is supported in current environment
 */
export function isOAuthSupported(): boolean {
  return true; // both WebView and normal web are supported with this setup
}
